import React from 'react';
import PropTypes from 'prop-types';
import uniqueId from 'lodash.uniqueid';
import { CircleCheckTranslateIcon } from '@ids/internal-icons';
import A11yFocus from '@ids/a11yfocus';
import { deviceUtils, styleOverrideReason } from '@ids/core';
import {
  Label,
  Input,
  IconContainer,
  InfoContainer,
  Title,
  CheckedIcon,
  Icons,
  Wrapper
} from './StyledTile';

const { node, func, string, oneOf, bool } = PropTypes;

/** */
export default class Tile extends React.Component {
  static propTypes = {
    /**
     * Unique Id for Tile
     */
    id: string,
    /**
     * icon component
     */
    icon: node.isRequired,
    /**
     * Callback for when Tile is clicked
     */
    onChange: func,
    /**
     * Title for the tile
     */
    title: string,
    /**
     * Additional info for the tile
     */
    description: string,
    /**
     * Size of the icon either small or large
     */
    iconSize: oneOf(['small', 'large']),
    /**
     * Bool to identify whether the tile is checked or not
     */
    // eslint-disable-next-line react/boolean-prop-naming
    checked: bool,
    /**
     * Id used for automation testing
     */
    automationId: string,
    /**
     * To determine whether tile is a radio or a checkbox
     */
    type: oneOf(['checkbox', 'radio']),
    /**
     * When `style` or `className` prop is provided, provide the reason behind the override.
     */
    styleOverrideReason
  };

  static defaultProps = {
    id: undefined,
    onChange: () => {},
    title: '',
    description: '',
    iconSize: 'large',
    checked: false,
    automationId: undefined,
    type: 'checkbox',
    styleOverrideReason: undefined
  };

  constructor(props) {
    super(props);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.mounted = false;
    this.isMobile = deviceUtils.isMobile() || deviceUtils.isTablet();
    this.labelId = this.props.id || uniqueId('idsTile');
  }

  componentDidMount() {
    this.mounted = true;
  }

  /**
   * Handles keyboard event
   */
  handleKeyDown(event) {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.props.onChange();
    }
  }

  /**
   * Renders different icons state
   */
  renderIcon = (icon, iconSize, checked, title, description, isMobile) => {
    if (checked && title) {
      return (
        <CheckedIcon iconSize={iconSize} isMobile={isMobile}>
          <CircleCheckTranslateIcon />
        </CheckedIcon>
      );
    }

    return (
      <Icons
        iconSize={iconSize}
        isMobile={isMobile}
        title={title}
        description={description}
      >
        {icon}
      </Icons>
    );
  };

  /**
   * Renders title & additional info
   */
  renderInfo = (title, description, isMobile) => (
    <InfoContainer title={title} isMobile={isMobile}>
      {title && <Title isMobile={isMobile}>{title}</Title>}
      {description && <p>{description}</p>}
    </InfoContainer>
  );

  render() {
    const {
      icon,
      iconSize,
      checked,
      title,
      description,
      automationId,
      type,
      onChange,
      id,
      ...restProps
    } = this.props;

    const tileId = id || this.labelId;

    return (
      <A11yFocus passA11yFocusProp>
        <Wrapper checked={checked} {...restProps}>
          <Input
            role={type}
            type={type}
            id={tileId}
            aria-checked={checked}
            onKeyDown={this.handleKeyDown}
          />
          <Label
            data-automation-id={automationId}
            htmlFor={tileId}
            isMobile={this.isMobile}
            title={title}
            checked={checked}
            description={description}
            onClick={onChange}
          >
            <IconContainer
              title={title}
              isMobile={this.isMobile}
              checked={checked}
              mounted={this.mounted}
              description={description}
            >
              {this.renderIcon(
                icon,
                iconSize,
                checked,
                title,
                description,
                this.isMobile
              )}
            </IconContainer>
            {(title || description) &&
              this.renderInfo(title, description, this.isMobile)}
          </Label>
        </Wrapper>
      </A11yFocus>
    );
  }
}
